import React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Check, X, Trash2, Mail, Ban, Download, MoreHorizontal } from 'lucide-react';

interface BulkAction {
  id: string;
  label: string;
  icon: React.ElementType;
  variant?: 'default' | 'destructive';
  action: () => void;
}

interface BulkActionsProps {
  selectedCount: number;
  actions: BulkAction[];
  onClearSelection: () => void;
  isSpanish?: boolean;
}

export const BulkActions: React.FC<BulkActionsProps> = ({
  selectedCount,
  actions,
  onClearSelection,
  isSpanish = false,
}) => {
  if (selectedCount === 0) return null;

  return (
    <div className="sticky top-0 z-20 bg-primary/10 border-b border-primary/20 px-4 py-3 flex items-center justify-between backdrop-blur-sm">
      <div className="flex items-center gap-3">
        <Badge variant="secondary" className="text-sm">
          {selectedCount} {isSpanish ? 'seleccionados' : 'selected'}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="h-8"
        >
          <X className="w-4 h-4 mr-1" />
          {isSpanish ? 'Limpiar' : 'Clear'}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        {actions.slice(0, 3).map((action) => {
          const Icon = action.icon;
          return (
            <Button
              key={action.id}
              variant={action.variant || 'default'}
              size="sm"
              onClick={action.action}
              className="h-8"
            >
              <Icon className="w-4 h-4 mr-1" />
              {action.label}
            </Button>
          );
        })}
        
        {actions.length > 3 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                {isSpanish ? 'Más acciones' : 'More actions'}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {actions.slice(3).map((action) => {
                const Icon = action.icon;
                return (
                  <DropdownMenuItem
                    key={action.id}
                    onClick={action.action}
                    className={action.variant === 'destructive' ? 'text-destructive' : ''}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {action.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
